import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import type { Redemption } from '@/types/reward';

type DecisionType = 'approve' | 'reject' | 'fulfill';

interface DecisionModalProps {
  open: boolean;
  type: DecisionType | null;
  redemption: Redemption | null;
  onClose: () => void;
  onConfirm: (note: string) => void;
}

const config: Record<DecisionType, { title: string; action: string; tone: string; hint: string }> = {
  approve: {
    title: 'Approve redemption',
    action: 'Approve',
    tone: 'bg-primary-500 hover:bg-primary-600',
    hint: 'The member\u2019s points have already been reserved. Approving will confirm the request.',
  },
  reject: {
    title: 'Reject redemption',
    action: 'Reject',
    tone: 'bg-secondary-500 hover:bg-secondary-600',
    hint: 'Rejecting refunds the reserved points back to the member\u2019s balance.',
  },
  fulfill: {
    title: 'Mark as fulfilled',
    action: 'Fulfill',
    tone: 'bg-accent-500 hover:bg-accent-600',
    hint: 'Confirm that the reward has been delivered to the member.',
  },
};

export default function DecisionModal({ open, type, redemption, onClose, onConfirm }: DecisionModalProps) {
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) setNote('');
  }, [open]);

  if (!type || !redemption) return null;
  const meta = config[type];

  return (
    <Modal open={open} title={meta.title} onClose={onClose}>
      <div className="flex flex-col gap-5">
        <div className="rounded-md bg-background-100 border border-background-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-heading font-semibold text-foreground-950 truncate">{redemption.reward}</p>
              <p className="text-sm text-foreground-500 truncate">
                {redemption.member} · {redemption.platform}
              </p>
            </div>
            <span className="shrink-0 font-heading font-bold text-secondary-700 whitespace-nowrap">
              -{redemption.pointsSpent.toLocaleString()} pts
            </span>
          </div>
        </div>

        <p className="text-sm text-foreground-500 leading-relaxed">{meta.hint}</p>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="decision-note" className="text-sm font-medium text-foreground-800">
            Note <span className="text-foreground-400 font-normal">(optional)</span>
          </label>
          <textarea
            id="decision-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Add an internal note about this decision..."
            className="w-full bg-background-50 border border-background-200 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
          />
          <p className="text-xs text-foreground-400 text-right">{note.length}/500</p>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap px-4 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(note.trim())}
            className={`whitespace-nowrap px-4 py-2.5 rounded-md text-background-50 text-sm font-semibold transition-colors cursor-pointer ${meta.tone}`}
          >
            {meta.action}
          </button>
        </div>
      </div>
    </Modal>
  );
}
